import { Box, Stack, Typography } from '@mui/material';
import React from 'react';

import { colors } from '../../theme/theme';
import { ContractFinancialSummaryResponse } from '../IMessageBubbleInterface';
import { formatCurrency, getContractStatusStyles } from '../MessageBubbleHelpers';

export const ContractFinancialSummaryCard: React.FC<{
    contractFinancialSummary: ContractFinancialSummaryResponse | null;
}> = ({ contractFinancialSummary }) => {
    if (!contractFinancialSummary) return null;

    return (
        <Box>
            <Typography variant="body2" sx={{ mb: 0.9, color: colors.fullBlack, fontWeight: 700, fontSize: '1rem' }}>
                {contractFinancialSummary.intro || 'Contract Information'}
            </Typography>

            <Stack gap={0.7}>
                {contractFinancialSummary.items.map(item => {
                    const normalizedStatus = (item.status || '').toLowerCase();
                    const statusStyles = normalizedStatus.includes('active')
                        ? getContractStatusStyles('closed')
                        : getContractStatusStyles(item.status || '');

                    return (
                        <Box
                            key={item.contractId}
                            sx={{
                                border: `1px solid ${colors.greyPreset[30]}`,
                                borderRadius: '12px',
                                backgroundColor: colors.fullWhite,
                                overflow: 'hidden',
                            }}
                        >
                            <Stack
                                direction="row"
                                alignItems="center"
                                justifyContent="space-between"
                                sx={{ px: 1, py: 0.55, backgroundColor: colors.bluePreset[10] }}
                            >
                                <Typography variant="body2" sx={{ fontWeight: 700, color: colors.fullWhite }}>
                                    #{item.contractId}
                                </Typography>
                                <Typography
                                    variant="caption"
                                    sx={{
                                        px: 1,
                                        py: 0.25,
                                        borderRadius: '999px',
                                        backgroundColor: statusStyles.bg,
                                        color: statusStyles.color,
                                        fontWeight: 700,
                                        fontSize: '0.72rem',
                                    }}
                                >
                                    {item.status || 'Unknown'}
                                </Typography>
                            </Stack>

                            <Stack sx={{ p: 0.9, gap: 0.6 }}>
                                <Typography variant="body2" sx={{ color: colors.fullBlack, fontWeight: 600 }}>
                                    {item.description || '-'}
                                </Typography>

                                <Box
                                    sx={{
                                        display: 'grid',
                                        gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                                        gap: 0.7,
                                    }}
                                >
                                    <Box>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                color: colors.greyPreset[40],
                                                fontSize: '0.72rem',
                                                fontWeight: 400,
                                            }}
                                        >
                                            FINANCED
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: colors.fullBlack, fontWeight: 700 }}>
                                            {item.financedAmount ? formatCurrency(item.financedAmount) : '-'}
                                        </Typography>
                                    </Box>
                                    <Box>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                color: colors.greyPreset[40],
                                                fontSize: '0.72rem',
                                                fontWeight: 400,
                                            }}
                                        >
                                            PAID (PRINCIPAL)
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: colors.fullBlack, fontWeight: 700 }}>
                                            {item.paidPrincipal ? formatCurrency(item.paidPrincipal) : '-'}
                                        </Typography>
                                    </Box>
                                    <Box>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                color: colors.greyPreset[40],
                                                fontSize: '0.72rem',
                                                fontWeight: 400,
                                            }}
                                        >
                                            REMAINING
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: colors.fullBlack, fontWeight: 700 }}>
                                            {item.remainingAmount ? formatCurrency(item.remainingAmount) : '-'}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Stack>
                        </Box>
                    );
                })}
            </Stack>

            {contractFinancialSummary.followUp && (
                <Typography variant="body2" sx={{ mt: 0.8, color: colors.fullBlack }}>
                    {contractFinancialSummary.followUp}
                </Typography>
            )}
        </Box>
    );
};
